/**
 * 票链云 — 权限清单与默认角色模板
 *
 * 文档 4.1 给出 6 个默认角色及其「可配置项」，4.2 给出三层权限模型。
 * 本文件把「哪个角色默认有哪些权限」固化成表，租户开通时按此播种，
 * 之后租户可自行增删改（文档明确要求角色可配置）。
 *
 * 设计取舍：默认权限给**最小可用集**而非最全集。
 * 例如财务记账岗默认拿不到审核权、技术实施组默认看不到发票金额，
 * 需要时由租户管理员显式授予——这比事后收紧更安全。
 */
import { DefaultRoleCode, PermissionCode, DataScope, RiskLevel } from './enums.js';
/** 权限清单（顺序即前端权限树顺序） */
export const PERMISSION_CATALOG = [
    // —— 租户与组织 ——
    { code: PermissionCode.TENANT_CONFIG_READ, name: '查看租户配置', module: '租户配置', description: '查看租户级参数、消息规则、OCR 字段模板' },
    { code: PermissionCode.TENANT_CONFIG_WRITE, name: '修改租户配置', module: '租户配置', description: '修改租户级参数与消息规则' },
    { code: PermissionCode.USER_READ, name: '查看用户', module: '组织权限', description: '查看租户内用户列表与详情' },
    { code: PermissionCode.USER_WRITE, name: '管理用户', module: '组织权限', description: '新增、编辑、停用用户与分配角色' },
    { code: PermissionCode.ROLE_READ, name: '查看角色', module: '组织权限', description: '查看角色与权限配置' },
    { code: PermissionCode.ROLE_WRITE, name: '管理角色', module: '组织权限', description: '新增、编辑、删除角色与权限分配' },
    { code: PermissionCode.DEPT_READ, name: '查看部门', module: '组织权限', description: '查看部门树' },
    { code: PermissionCode.DEPT_WRITE, name: '管理部门', module: '组织权限', description: '新增、编辑、删除部门' },
    // —— 流程配置 ——
    { code: PermissionCode.FLOW_TEMPLATE_READ, name: '查看流程模板', module: '流程配置', description: '查看审批流程模板与节点' },
    { code: PermissionCode.FLOW_TEMPLATE_WRITE, name: '管理流程模板', module: '流程配置', description: '自定义审批节点、条件分支、退回规则' },
    // —— 发票 ——
    { code: PermissionCode.INVOICE_CREATE, name: '上传发票', module: '发票管理', description: '上传发票原件并发起校验流程' },
    { code: PermissionCode.INVOICE_READ, name: '查看发票', module: '发票管理', description: '查看发票列表与详情' },
    { code: PermissionCode.INVOICE_UPDATE_OCR, name: '修正识别结果', module: '发票管理', description: '人工编辑 OCR 识别结果（修改留痕）' },
    { code: PermissionCode.INVOICE_SUBMIT, name: '提交校验', module: '发票管理', description: '提交发票进入自动校验流程' },
    { code: PermissionCode.INVOICE_DELETE, name: '删除发票', module: '发票管理', description: '删除未过审的发票（记录、复印件与原件文件一并删除，不可恢复）' },
    { code: PermissionCode.INVOICE_ATTACHMENT_WRITE, name: '管理发票附件', module: '发票管理', description: '上传/删除发票复印件，修正提取出的手写内容（修改留痕）' },
    { code: PermissionCode.INVOICE_ATTACHMENT_READ, name: '查看发票附件', module: '发票管理', description: '查看发票复印件与提取出的手写内容' },
    // —— 校验 ——
    { code: PermissionCode.CHECK_READ, name: '查看校验报告', module: '校验核验', description: '查看 OCR、企查查、税务、合同比对结果' },
    { code: PermissionCode.CHECK_RETRY, name: '重试校验', module: '校验核验', description: '对校验失败的项重新发起校验' },
    { code: PermissionCode.CHECK_MANUAL_OVERRIDE, name: '人工接管校验', module: '校验核验', description: '外部校验不可用时人工核验并填写说明' },
    // —— 审核 ——
    { code: PermissionCode.INVOICE_REVIEW, name: '财务初审', module: '审核管理', description: '审阅校验报告、通过或退回发票' },
    { code: PermissionCode.INVOICE_SPECIAL_RELEASE, name: '特例放行', module: '审核管理', description: '红冲、作废等高风险发票的专项审批放行（留痕）' },
    // —— 记账与 T+ ——
    { code: PermissionCode.TPLUS_PUSH, name: '推送T+', module: '记账同步', description: '将初审通过的发票推送至 T+ 待办业务池' },
    { code: PermissionCode.TPLUS_READ, name: '查看T+同步', module: '记账同步', description: '查看同步状态与凭证回写结果' },
    { code: PermissionCode.TAX_INPUT_LOCK, name: '进项锁定', module: '记账同步', description: '执行进项发票锁定（不可逆写操作，影响纳税申报）' },
    // —— 合同 ——
    { code: PermissionCode.CONTRACT_READ, name: '查看合同', module: '合同管理', description: '查看合同信息与关联发票' },
    { code: PermissionCode.CONTRACT_WRITE, name: '管理合同', module: '合同管理', description: '新增、编辑合同' },
    { code: PermissionCode.CONTRACT_REVIEW, name: '合同经营审核', module: '合同管理', description: '合同条款、金额、结算方式的经营部审核' },
    { code: PermissionCode.CONTRACT_FINANCE_REVIEW, name: '合同财务审核', module: '合同管理', description: '合同金额、税号、结算与发票条款的财务审核' },
    { code: PermissionCode.CONTRACT_LEGAL_REVIEW, name: '合同法务审核', module: '合同管理', description: '合同条款风险与合规性审核' },
    { code: PermissionCode.CONTRACT_APPROVE, name: '合同领导审批', module: '合同管理', description: '超金额阈值合同的终审' },
    { code: PermissionCode.CONTRACT_PAYMENT_WRITE, name: '登记合同付款', module: '合同管理', description: '登记付款申请、确认付款执行' },
    { code: PermissionCode.CONTRACT_EVALUATE, name: '履约评价', module: '合同管理', description: '对供应商/分包商做履约评价' },
    { code: PermissionCode.CONTRACT_EXPORT, name: '导出合同台账', module: '合同管理', description: '导出合同台账与执行报表（审计留痕）' },
    { code: PermissionCode.CONTRACT_ALERT_HANDLE, name: '处理合同预警', module: '合同管理', description: '确认或忽略合同预警（忽略需填理由）' },
    // —— 费用报销 ——
    { code: PermissionCode.EXPENSE_READ, name: '查看费用单据', module: '费用报销', description: '查看费用申请与报销单' },
    { code: PermissionCode.EXPENSE_APPLY, name: '费用申请', module: '费用报销', description: '提交事前费用申请' },
    { code: PermissionCode.EXPENSE_REIMBURSE, name: '提交报销', module: '费用报销', description: '填写报销单、上传发票、提交审批' },
    { code: PermissionCode.EXPENSE_DEPT_APPROVE, name: '报销部门审批', module: '费用报销', description: '部门负责人对业务真实性的审批' },
    { code: PermissionCode.EXPENSE_REVIEW, name: '报销财务初审', module: '费用报销', description: '发票合规、金额与预算的财务初审' },
    { code: PermissionCode.EXPENSE_RECHECK, name: '报销财务复核', module: '费用报销', description: '大额或异常报销单的财务复核' },
    { code: PermissionCode.EXPENSE_APPROVE, name: '报销领导审批', module: '费用报销', description: '超金额阈值报销单的终审' },
    { code: PermissionCode.EXPENSE_PAY, name: '报销付款', module: '费用报销', description: '出纳登记并执行付款' },
    { code: PermissionCode.EXPENSE_BUDGET_MANAGE, name: '预算管理', module: '费用报销', description: '编制预算、提交与审批预算调整' },
    { code: PermissionCode.EXPENSE_REPORT, name: '费用报表', module: '费用报销', description: '查看费用明细、汇总、预算执行与趋势报表' },
    { code: PermissionCode.EXPENSE_EXPORT, name: '导出费用数据', module: '费用报销', description: '导出费用报表与台账（审计留痕）' },
    { code: PermissionCode.EXPENSE_ALERT_HANDLE, name: '处理费用预警', module: '费用报销', description: '确认或忽略预算与超时预警（忽略需填理由）' },
    // —— 档案 ——
    { code: PermissionCode.ARCHIVE_READ, name: '查看档案', module: '档案库', description: '检索发票档案与追溯链路' },
    { code: PermissionCode.ARCHIVE_EXPORT, name: '导出档案', module: '档案库', description: '导出档案数据（审计留痕）' },
    // —— 接口与日志 ——
    { code: PermissionCode.PROVIDER_CONFIG_READ, name: '查看接口配置', module: '接口与日志', description: '查看外部接口配置与配额' },
    { code: PermissionCode.PROVIDER_CONFIG_WRITE, name: '管理接口配置', module: '接口与日志', description: '配置外部接口开关、参数与凭证' },
    { code: PermissionCode.PROVIDER_LOG_READ, name: '查看调用日志', module: '接口与日志', description: '查看外部接口调用日志与原始报文' },
    // —— 消息 ——
    { code: PermissionCode.MESSAGE_READ, name: '查看消息', module: '消息中心', description: '查看待办与风险提醒消息' },
];
/** 权限码 → 展示信息的索引 */
export const PERMISSION_BY_CODE = Object.fromEntries(PERMISSION_CATALOG.map((p) => [p.code, p]));
/** 全部权限码 */
export const ALL_PERMISSION_CODES = PERMISSION_CATALOG.map((p) => p.code);
/**
 * 11 个默认角色模板（文档 4.1 + 延伸需求 4「权限设计」）。
 *
 * 权限分配依据文档中每个角色的「关键节点职责」逐条对应：
 *   项目部经办人   → 上传、关联合同、处理退回
 *   财务初审岗     → 审阅报告、复核、通过/退回、确认推送；合同财务审核、报销财务初审
 *   财务记账岗     → T+ 端操作，本系统内主要查看同步结果；报销财务复核
 *   法务岗         → 合同风险、归档、事后抽查；合同法务审核
 *   技术实施组     → 接口配置、日志查看、异常处理
 *   经营部经办     → 合同登记、变更、提交审批（延伸需求第一部分）
 *   部门负责人     → 费用申请与报销的一级审批（延伸需求 3.4）
 *   领导           → 超金额阈值的终审
 *   出纳           → 付款执行
 *   普通员工       → 只能看与提交本人单据
 *   租户管理员     → 全部
 *
 * 【为什么新角色的权限给得克制】
 * 新角色建出来**不会自动分配给任何人**，但一旦分配就等于授予了这些权限。
 * 按最小可用集给：领导不给合同登记、出纳不给审批、员工只能看本人（`SELF` 数据范围）。
 */
export const DEFAULT_ROLES = [
    {
        code: DefaultRoleCode.TENANT_ADMIN,
        name: '租户管理员',
        description: '租户内全部配置与用户管理',
        dataScope: DataScope.TENANT,
        amountLimitCents: null,
        maxRiskLevel: null,
        permissions: [...ALL_PERMISSION_CODES],
    },
    {
        code: DefaultRoleCode.PROJECT_HANDLER,
        name: '项目部经办人',
        description: '上传发票、关联合同、处理退回',
        // 文档 4.1：可上传的发票类型、可关联合同范围可配置
        dataScope: DataScope.DEPT,
        amountLimitCents: null,
        maxRiskLevel: null,
        permissions: [
            PermissionCode.INVOICE_CREATE,
            PermissionCode.INVOICE_READ,
            PermissionCode.INVOICE_UPDATE_OCR,
            PermissionCode.INVOICE_SUBMIT,
            PermissionCode.INVOICE_DELETE,
            // 经办人最常拿到纸质票，补传复印件与订正手写内容应由他完成
            PermissionCode.INVOICE_ATTACHMENT_WRITE,
            PermissionCode.INVOICE_ATTACHMENT_READ,
            PermissionCode.CHECK_READ,
            PermissionCode.CONTRACT_READ,
            PermissionCode.ARCHIVE_READ,
            /*
             * 【为什么项目部经办人也要报账的权限】他是"常年在工地上的人"——
             * 差旅、招待、零星采购多数由他垫付。不给他申请与报销的口子，
             * 这些费用会绕到别人的账号下提交，谁真花的钱反而查不出来。
             * 数据范围是 DEPT，看不到别的部门的报销单。
             */
            PermissionCode.EXPENSE_READ,
            PermissionCode.EXPENSE_APPLY,
            PermissionCode.EXPENSE_REIMBURSE,
            PermissionCode.MESSAGE_READ,
        ],
    },
    {
        code: DefaultRoleCode.FINANCE_REVIEWER,
        name: '财务初审岗',
        description: '审阅校验报告、人工复核、确认推送',
        dataScope: DataScope.TENANT,
        // 默认 50 万元审核上限，超过需上级或管理员处理
        amountLimitCents: 50_000_000,
        // 默认可放行到中风险；高风险（红冲/作废）需特例放行权限
        maxRiskLevel: RiskLevel.MEDIUM,
        permissions: [
            PermissionCode.INVOICE_READ,
            PermissionCode.INVOICE_UPDATE_OCR,
            // 初审时既要能看复印件与手写内容，也要能订正识别错的行——
            // 与已有的 INVOICE_UPDATE_OCR 同一层职责，不额外放宽
            PermissionCode.INVOICE_ATTACHMENT_WRITE,
            PermissionCode.INVOICE_ATTACHMENT_READ,
            PermissionCode.INVOICE_REVIEW,
            PermissionCode.INVOICE_SPECIAL_RELEASE,
            PermissionCode.CHECK_READ,
            PermissionCode.CHECK_RETRY,
            PermissionCode.CHECK_MANUAL_OVERRIDE,
            PermissionCode.TPLUS_PUSH,
            PermissionCode.TPLUS_READ,
            PermissionCode.CONTRACT_READ,
            // 合同审批链的财务段（需求 3.2），与发票初审同一岗位
            PermissionCode.CONTRACT_FINANCE_REVIEW,
            // 合同付款的执行与确认。归属财务初审岗而不是出纳：
            // 需求 4 的合同权限表里没有出纳，而合同付款是财务口径的事
            // （出纳只管报销付款，见 CASHIER 的权限表）
            PermissionCode.CONTRACT_PAYMENT_WRITE,
            PermissionCode.EXPENSE_READ,
            // 报销审批链的财务初审段（需求 3.4）
            PermissionCode.EXPENSE_REVIEW,
            /*
             * 【为什么财务初审要管预算】他审报销时要对着预算看余量（需求 3.6 的
             * 「超预算预警」就发生在这一步），而预算编制与调整在本平台的岗位体系里
             * 没有比"财务口径的负责人"更合适的归属——财务初审岗就是这个角色。
             * 报表同理：审完的结论要能被自己复盘。
             */
            PermissionCode.EXPENSE_BUDGET_MANAGE,
            PermissionCode.EXPENSE_REPORT,
            PermissionCode.ARCHIVE_READ,
            PermissionCode.ARCHIVE_EXPORT,
            PermissionCode.MESSAGE_READ,
        ],
    },
    {
        code: DefaultRoleCode.FINANCE_BOOKKEEPER,
        name: '财务记账岗',
        description: 'T+ 端选择科目、生成凭证、回写',
        dataScope: DataScope.TENANT,
        amountLimitCents: null,
        maxRiskLevel: null,
        permissions: [
            PermissionCode.INVOICE_READ,
            PermissionCode.CHECK_READ,
            PermissionCode.TPLUS_READ,
            // 记账时需要对得上复印件上的签收信息，因此给读权限，但不给改
            PermissionCode.INVOICE_ATTACHMENT_READ,
            // 进项锁定默认**不给**——不可逆写操作，需管理员显式授予
            PermissionCode.CONTRACT_READ,
            // 报销审批链的复核段（需求 3.4：大额或异常交财务记账岗复核）
            PermissionCode.EXPENSE_READ,
            PermissionCode.EXPENSE_RECHECK,
            PermissionCode.ARCHIVE_READ,
            PermissionCode.MESSAGE_READ,
        ],
    },
    {
        code: DefaultRoleCode.LEGAL,
        name: '法务岗',
        description: '合同风险、归档、事后抽查',
        dataScope: DataScope.TENANT,
        amountLimitCents: null,
        maxRiskLevel: null,
        permissions: [
            PermissionCode.INVOICE_READ,
            PermissionCode.CHECK_READ,
            // 事后抽查要能看到复印件上的手写批注，但取证以原件为准，不给改
            PermissionCode.INVOICE_ATTACHMENT_READ,
            PermissionCode.CONTRACT_READ,
            PermissionCode.CONTRACT_WRITE,
            // 合同审批链的法务段（需求 3.2：条款风险、合规性）
            PermissionCode.CONTRACT_LEGAL_REVIEW,
            PermissionCode.ARCHIVE_READ,
            PermissionCode.ARCHIVE_EXPORT,
            PermissionCode.MESSAGE_READ,
        ],
    },
    {
        code: DefaultRoleCode.TECH_OPS,
        name: '技术实施组',
        description: '接口配置、日志查看、异常处理',
        dataScope: DataScope.TENANT,
        amountLimitCents: null,
        maxRiskLevel: null,
        permissions: [
            // 刻意不给发票读权限：技术组排查接口问题看调用日志即可，无需接触票面金额
            PermissionCode.PROVIDER_CONFIG_READ,
            PermissionCode.PROVIDER_CONFIG_WRITE,
            PermissionCode.PROVIDER_LOG_READ,
            PermissionCode.TENANT_CONFIG_READ,
            PermissionCode.FLOW_TEMPLATE_READ,
            PermissionCode.USER_READ,
            PermissionCode.MESSAGE_READ,
        ],
    },
    {
        code: DefaultRoleCode.BUSINESS_HANDLER,
        name: '经营部经办',
        description: '合同登记、变更、提交审批',
        // 需求 4：经营部数据权限为「本部门合同」
        dataScope: DataScope.DEPT,
        amountLimitCents: null,
        maxRiskLevel: null,
        permissions: [
            PermissionCode.CONTRACT_READ,
            PermissionCode.CONTRACT_WRITE,
            // 审批链的第一段：条款、金额、结算方式的经营部审核
            PermissionCode.CONTRACT_REVIEW,
            PermissionCode.CONTRACT_EVALUATE,
            PermissionCode.CONTRACT_ALERT_HANDLE,
            PermissionCode.CONTRACT_EXPORT,
            PermissionCode.EXPENSE_READ,
            PermissionCode.EXPENSE_APPLY,
            PermissionCode.INVOICE_READ,
            PermissionCode.MESSAGE_READ,
        ],
    },
    {
        code: DefaultRoleCode.DEPT_HEAD,
        name: '部门负责人',
        description: '本部门费用申请与报销的业务真实性审批',
        // 只审本部门：数据范围 DEPT，加上 flow.router 的业务对象部门校验
        dataScope: DataScope.DEPT,
        /**
         * **不设金额上限**。
         *
         * 一开始按需求 3.4 的示例给这一项配了 5000 元（"5000 元以下部门负责人 +
         * 财务初审，5000 元以上加领导审批"）。那是**误读**：需求说的是
         * "5000 元以上**加**领导审批"——领导那一段是**叠加**的，
         * 不是替换。所有单据都要先过部门负责人。
         *
         * 于是配了上限就会死锁：一张 6000 元的报销在部门负责人这一步被打回
         * （超上限），而它恰恰是需求要求"该继续往上走"的那一种。
         * 实际测出来的现象就是审批链在第二个节点 422 卡死。
         *
         * 结论：**这些链路的金额控制由流程模板的条件分支表达**（可在「流程配置」里改），
         * 角色上限只留作"某个角色最多能批多少"的兜底，默认不设。
         */
        amountLimitCents: null,
        maxRiskLevel: null,
        permissions: [
            PermissionCode.EXPENSE_READ,
            PermissionCode.EXPENSE_APPLY,
            PermissionCode.EXPENSE_REIMBURSE,
            PermissionCode.EXPENSE_DEPT_APPROVE,
            PermissionCode.EXPENSE_REPORT,
            PermissionCode.CONTRACT_READ,
            PermissionCode.MESSAGE_READ,
        ],
    },
    {
        code: DefaultRoleCode.LEADER,
        name: '领导',
        description: '超金额阈值合同的终审、超阈值报销的终审',
        dataScope: DataScope.TENANT,
        // 终审不设上限，阈值由流程模板的条件分支决定
        amountLimitCents: null,
        maxRiskLevel: null,
        permissions: [
            PermissionCode.CONTRACT_READ,
            PermissionCode.CONTRACT_APPROVE,
            PermissionCode.CONTRACT_EVALUATE,
            PermissionCode.CONTRACT_EXPORT,
            PermissionCode.CONTRACT_ALERT_HANDLE,
            PermissionCode.EXPENSE_READ,
            PermissionCode.EXPENSE_APPROVE,
            PermissionCode.EXPENSE_REPORT,
            PermissionCode.EXPENSE_EXPORT,
            PermissionCode.EXPENSE_ALERT_HANDLE,
            PermissionCode.INVOICE_READ,
            PermissionCode.CHECK_READ,
            PermissionCode.MESSAGE_READ,
        ],
    },
    {
        code: DefaultRoleCode.CASHIER,
        name: '出纳',
        description: '报销付款执行',
        dataScope: DataScope.TENANT,
        amountLimitCents: null,
        maxRiskLevel: null,
        permissions: [
            PermissionCode.EXPENSE_READ,
            PermissionCode.EXPENSE_PAY,
            PermissionCode.EXPENSE_REPORT,
            PermissionCode.MESSAGE_READ,
        ],
    },
    {
        code: DefaultRoleCode.EMPLOYEE,
        name: '普通员工',
        description: '提交并查看本人的费用申请与报销单',
        // 只能看本人：数据范围 SELF，与发票列表的「仅本人」同一套机制
        dataScope: DataScope.SELF,
        amountLimitCents: null,
        maxRiskLevel: null,
        permissions: [
            PermissionCode.EXPENSE_READ,
            PermissionCode.EXPENSE_APPLY,
            PermissionCode.EXPENSE_REIMBURSE,
            PermissionCode.MESSAGE_READ,
        ],
    },
];
/** 取默认角色的定义 */
export function getDefaultRole(code) {
    return DEFAULT_ROLES.find((r) => r.code === code);
}
/** 判断权限码是否存在（防止配置里写错权限码） */
export function isValidPermission(code) {
    return code in PERMISSION_BY_CODE;
}
//# sourceMappingURL=permissions.js.map